/**
 * Audit log viewer (internal only).
 *
 *   - Filters by action, actor email, scope, target, date range.
 *   - Cursor pagination (newest-first).
 *   - Click a row to expand the before/after JSON inline.
 *   - The Settings/Wallet/Disbursement/etc. detail diffs live in `after`;
 *     login rows put the failure reason there.
 */
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Code,
  Collapse,
  Grid,
  Group,
  Loader,
  MultiSelect,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconAlertCircle,
  IconBuildingStore,
  IconCash,
  IconChevronDown,
  IconChevronRight,
  IconCode,
  IconEye,
  IconFilter,
  IconFilterOff,
  IconLogin,
  IconLogout,
  IconLogout2,
  IconRefresh,
  IconRotate,
  IconSend,
  IconSettings,
  IconShieldCheck,
  IconShieldLock,
  IconTransfer,
  IconUserPlus,
} from '@tabler/icons-react';
import { formatIdr } from '../lib/format';
import { auditLogApi, type AuditLogRow } from '../api/auditLog';
import { formatGmt7 } from '../lib/format';
import { ApiCallError } from '../api/client';
import { usePermissions } from '../auth/SessionContext';
import { useTranslation } from '../i18n/LocaleContext';
import { DataTable } from '../components/DataTable';
import { JsonView } from '../components/JsonView';
import type { TranslationKey } from '../i18n/translations';

type TFn = ReturnType<typeof useTranslation>['t'];

type FilterDraft = {
  action: string;
  actorEmail: string;
  actorScope: Array<'internal' | 'external' | 'system'>;
  target: string;
  dateRange: [Date | null, Date | null];
};

const EMPTY: FilterDraft = {
  action: '',
  actorEmail: '',
  actorScope: [],
  target: '',
  dateRange: [null, null],
};

function toIsoDate(d: Date | null): string | undefined {
  if (!d) return undefined;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function draftToFilter(d: FilterDraft) {
  return {
    action: d.action.trim() || undefined,
    actorEmail: d.actorEmail.trim() || undefined,
    actorScope: d.actorScope.length > 0 ? d.actorScope : undefined,
    target: d.target.trim() || undefined,
    fromDate: toIsoDate(d.dateRange[0]),
    toDate: toIsoDate(d.dateRange[1]),
    limit: 50,
  };
}

function countActiveFilters(d: FilterDraft): number {
  let n = 0;
  if (d.action.trim()) n += 1;
  if (d.actorEmail.trim()) n += 1;
  if (d.actorScope.length > 0) n += 1;
  if (d.target.trim()) n += 1;
  if (d.dateRange[0] || d.dateRange[1]) n += 1;
  return n;
}

function scopeLabel(scope: string, t: TFn): string {
  if (scope === 'internal') return t('auditLog.scopeInternal');
  if (scope === 'external') return t('auditLog.scopeExternal');
  if (scope === 'system') return t('auditLog.scopeSystem');
  return scope;
}

function scopeColor(scope: string): string {
  if (scope === 'internal') return 'blue';
  if (scope === 'external') return 'brand';
  if (scope === 'system') return 'gray';
  return 'gray';
}

type ActionDescriptor = {
  /** Translation key for the human-friendly activity label. */
  labelKey: TranslationKey;
  /** One-sentence narrative; receives the after-payload and translator. */
  narrative: (after: Record<string, unknown>, t: TFn) => string;
  icon: typeof IconLogin;
  color: string;
};

const ACTION_DESCRIPTORS: Record<string, ActionDescriptor> = {
  'auth.login.success': {
    labelKey: 'auditLog.actLoginSuccessLabel',
    narrative: (_a, t) => t('auditLog.actLoginSuccessNarrative'),
    icon: IconLogin,
    color: 'green',
  },
  'auth.login.failed': {
    labelKey: 'auditLog.actLoginFailedLabel',
    narrative: (a, t) => {
      const reason = String(a['reason'] ?? '');
      const map: Record<string, TranslationKey> = {
        wrong_password: 'auditLog.reasonWrongPassword',
        unknown_email: 'auditLog.reasonUnknownEmail',
        ip_not_whitelisted: 'auditLog.reasonIpNotWhitelisted',
        ip_whitelist_not_configured: 'auditLog.reasonIpWhitelistNotConfigured',
        ip_unknown: 'auditLog.reasonIpUnknown',
      };
      const reasonKey = map[reason];
      return t('auditLog.actLoginFailedNarrative', { reason: reasonKey ? t(reasonKey) : reason });
    },
    icon: IconShieldLock,
    color: 'red',
  },
  'auth.logout': {
    labelKey: 'auditLog.actLogoutLabel',
    narrative: (_a, t) => t('auditLog.actLogoutNarrative'),
    icon: IconLogout,
    color: 'gray',
  },
  'cashin.create': {
    labelKey: 'auditLog.actCashinCreateLabel',
    narrative: (a, t) =>
      t('auditLog.actCashinCreateNarrative', {
        amount: formatIdr(Number(a['amount'] ?? 0)),
        method: String(a['method'] ?? '?'),
      }),
    icon: IconCash,
    color: 'teal',
  },
  'disbursement.inquiry': {
    labelKey: 'auditLog.actDisbursementInquiryLabel',
    narrative: (a, t) =>
      t('auditLog.actDisbursementInquiryNarrative', {
        accountNumber: String(a['accountNumber'] ?? '?'),
        bank: String(a['bankName'] ?? a['bankCode'] ?? '?'),
        amount: formatIdr(Number(a['amount'] ?? 0)),
      }),
    icon: IconEye,
    color: 'grape',
  },
  'disbursement.create': {
    labelKey: 'auditLog.actDisbursementCreateLabel',
    narrative: (a, t) =>
      t('auditLog.actDisbursementCreateNarrative', {
        amount: formatIdr(Number(a['amount'] ?? 0)),
        holderName: String(a['holderName'] ?? '?'),
        bank: String(a['bankName'] ?? a['bankCode'] ?? '?'),
        accountNumber: String(a['accountNumber'] ?? '?'),
      }),
    icon: IconTransfer,
    color: 'grape',
  },
  'merchant.create': {
    labelKey: 'auditLog.actMerchantCreateLabel',
    narrative: (a, t) => t('auditLog.actMerchantCreateNarrative', { name: String(a['name'] ?? '?') }),
    icon: IconBuildingStore,
    color: 'indigo',
  },
  'merchant.update': {
    labelKey: 'auditLog.actMerchantUpdateLabel',
    narrative: (_a, t) => t('auditLog.actMerchantUpdateNarrative'),
    icon: IconBuildingStore,
    color: 'indigo',
  },
  'merchant.credentials.reveal': {
    labelKey: 'auditLog.actMerchantRevealLabel',
    narrative: (_a, t) => t('auditLog.actMerchantRevealNarrative'),
    icon: IconEye,
    color: 'orange',
  },
  'merchant.regenerate-credentials': {
    labelKey: 'auditLog.actMerchantRegenLabel',
    narrative: (a, t) =>
      t('auditLog.actMerchantRegenNarrative', { newSuffix: String(a['newSuffix'] ?? '?') }),
    icon: IconRotate,
    color: 'yellow',
  },
  'merchant.ip-whitelist.add': {
    labelKey: 'auditLog.actMerchantIpAddLabel',
    narrative: (a, t) => t('auditLog.actMerchantIpAddNarrative', { cidr: String(a['cidr'] ?? '?') }),
    icon: IconShieldCheck,
    color: 'cyan',
  },
  'user.ip-whitelist.add': {
    labelKey: 'auditLog.actUserIpAddLabel',
    narrative: (a, t) => t('auditLog.actUserIpAddNarrative', { cidr: String(a['cidr'] ?? '?') }),
    icon: IconShieldCheck,
    color: 'cyan',
  },
  'users.create': {
    labelKey: 'auditLog.actUsersCreateLabel',
    narrative: (a, t) => t('auditLog.actUsersCreateNarrative', { email: String(a['email'] ?? '?') }),
    icon: IconUserPlus,
    color: 'blue',
  },
  'roles.permissions.update': {
    labelKey: 'auditLog.actRolesUpdateLabel',
    narrative: (a, t) =>
      t('auditLog.actRolesUpdateNarrative', { roleSlug: String(a['roleSlug'] ?? a['slug'] ?? '?') }),
    icon: IconShieldCheck,
    color: 'pink',
  },
  'webhook.deposit.resend': {
    labelKey: 'auditLog.actWebhookDepositResendLabel',
    narrative: (_a, t) => t('auditLog.actWebhookDepositResendNarrative'),
    icon: IconSend,
    color: 'cyan',
  },
  'webhook.disbursement.resend': {
    labelKey: 'auditLog.actWebhookDisbursementResendLabel',
    narrative: (_a, t) => t('auditLog.actWebhookDisbursementResendNarrative'),
    icon: IconSend,
    color: 'cyan',
  },
  'settings.update': {
    labelKey: 'auditLog.actSettingsUpdateLabel',
    narrative: (a, t) => t('auditLog.actSettingsUpdateNarrative', { key: String(a['key'] ?? '?') }),
    icon: IconSettings,
    color: 'pink',
  },
  'settings.reset': {
    labelKey: 'auditLog.actSettingsResetLabel',
    narrative: (a, t) => t('auditLog.actSettingsResetNarrative', { key: String(a['key'] ?? '?') }),
    icon: IconSettings,
    color: 'pink',
  },
};

function describeAction(action: string, after: unknown, t: TFn): {
  label: string;
  narrative: string;
  icon: typeof IconLogin;
  color: string;
} {
  const def = ACTION_DESCRIPTORS[action];
  if (def) {
    return {
      label: t(def.labelKey),
      narrative: def.narrative((after as Record<string, unknown>) ?? {}, t),
      icon: def.icon,
      color: def.color,
    };
  }
  // Fallback: use the raw action key, no narrative.
  return {
    label: action,
    narrative: '',
    icon: IconLogout2,
    color: 'gray',
  };
}

/**
 * Renders the `after` payload as labeled key→value pairs. Key formatting maps
 * common technical keys to Indonesian labels. Unknown keys are humanised
 * (camelCase → "Camel case"). Money fields are detected by name and rendered
 * via formatIdr; references like trxId / merchantOrderId stay monospace.
 */
function renderAfterFields(after: unknown, t: TFn): React.ReactNode {
  if (after == null || typeof after !== 'object') return null;
  const obj = after as Record<string, unknown>;
  const entries = Object.entries(obj);
  if (entries.length === 0) {
    return <Text size="xs" c="dimmed">{t('auditLog.noExtraDetail')}</Text>;
  }
  return (
    <Stack gap={4}>
      {entries.map(([k, v]) => (
        <Group key={k} gap="xs" wrap="nowrap" align="flex-start">
          <Text size="xs" c="dimmed" style={{ minWidth: 150 }}>
            {fieldLabel(k, t)}
          </Text>
          <Text size="xs" style={{ flex: 1, wordBreak: 'break-all' }} ff={isMonoField(k) ? 'monospace' : undefined}>
            {formatValue(k, v, t)}
          </Text>
        </Group>
      ))}
    </Stack>
  );
}

const FIELD_LABELS: Record<string, TranslationKey> = {
  amount: 'auditLog.fieldAmount',
  fee: 'auditLog.fieldFee',
  totalDebit: 'auditLog.fieldTotalDebit',
  merchantRevenue: 'auditLog.fieldMerchantRevenue',
  bankCode: 'auditLog.fieldBankCode',
  bankName: 'auditLog.fieldBankName',
  accountNumber: 'auditLog.fieldAccountNumber',
  holderName: 'auditLog.fieldHolderName',
  verifiedHolderName: 'auditLog.fieldVerifiedHolderName',
  merchantId: 'auditLog.fieldMerchantId',
  trxId: 'auditLog.fieldTrxId',
  referenceId: 'auditLog.fieldReferenceId',
  payokPlatformOrderId: 'auditLog.fieldPayokPlatformOrderId',
  payokMerchantOrderId: 'auditLog.fieldPayokMerchantOrderId',
  userId: 'auditLog.fieldUserId',
  userType: 'auditLog.fieldUserType',
  isSuperuser: 'auditLog.fieldIsSuperuser',
  reason: 'auditLog.fieldReason',
  method: 'auditLog.fieldMethod',
  ip: 'auditLog.fieldIp',
  cidr: 'auditLog.fieldCidr',
  newSuffix: 'auditLog.fieldNewSuffix',
  key: 'auditLog.fieldKey',
  email: 'auditLog.fieldEmail',
  name: 'auditLog.fieldName',
  jti: 'auditLog.fieldJti',
  newOutboxId: 'auditLog.fieldNewOutboxId',
  newDeliveryId: 'auditLog.fieldNewDeliveryId',
  sourceOutboxId: 'auditLog.fieldSourceOutboxId',
  sourceStatus: 'auditLog.fieldSourceStatus',
  targetUrl: 'auditLog.fieldTargetUrl',
  remark: 'auditLog.fieldRemark',
};

function fieldLabel(key: string, t: TFn): string {
  if (FIELD_LABELS[key]) return t(FIELD_LABELS[key]);
  // Humanize camelCase: "lastUsedAt" → "Last used at"
  const spaced = key.replace(/([A-Z])/g, ' $1').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const MONEY_FIELDS = new Set(['amount', 'fee', 'totalDebit', 'merchantRevenue', 'transFee', 'totalTransFee']);
const MONO_FIELDS = new Set([
  'trxId', 'merchantId', 'userId', 'jti', 'requestId', 'payokPlatformOrderId',
  'payokMerchantOrderId', 'newOutboxId', 'sourceOutboxId', 'newDeliveryId',
  'cidr', 'ip',
]);

function isMonoField(key: string): boolean {
  return MONO_FIELDS.has(key);
}

function formatValue(key: string, v: unknown, t: TFn): React.ReactNode {
  if (v == null) return <Text component="span" size="xs" c="dimmed">—</Text>;
  if (MONEY_FIELDS.has(key) && typeof v === 'number') return formatIdr(v);
  if (typeof v === 'boolean') return v ? t('auditLog.boolYes') : t('auditLog.boolNo');
  if (typeof v === 'object') {
    return (
      <Code style={{ fontSize: 11 }}>
        {JSON.stringify(v)}
      </Code>
    );
  }
  return String(v);
}

export function AuditLogPage() {
  const { t } = useTranslation();
  const { has, isSuperuser } = usePermissions();
  const canView = isSuperuser || has('audit.view');

  const [filter, setFilter] = useState<FilterDraft>(EMPTY);
  const [activeFilter, setActiveFilter] = useState(() => draftToFilter(EMPTY));
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const [pageIdx, setPageIdx] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const actionsQuery = useQuery({
    queryKey: ['audit-log-actions'],
    queryFn: () => auditLogApi.actions(),
    enabled: canView,
    retry: 0,
    staleTime: 60_000,
  });

  const queryFilter = {
    ...activeFilter,
    cursor: cursorStack[pageIdx] ?? undefined,
  };
  const pageQuery = useQuery({
    queryKey: ['audit-log-page', queryFilter],
    queryFn: () => auditLogApi.page(queryFilter),
    enabled: canView,
    retry: 0,
    placeholderData: (prev) => prev,
  });

  const applyFilters = () => {
    setActiveFilter(draftToFilter(filter));
    setCursorStack([null]);
    setPageIdx(0);
  };
  const clearFilters = () => {
    setFilter(EMPTY);
    setActiveFilter(draftToFilter(EMPTY));
    setCursorStack([null]);
    setPageIdx(0);
  };
  const nextPage = () => {
    const c = pageQuery.data?.nextCursor;
    if (c) {
      setCursorStack((s) => [...s.slice(0, pageIdx + 1), c]);
      setPageIdx((i) => i + 1);
    }
  };
  const prevPage = () => {
    if (pageIdx > 0) setPageIdx((i) => i - 1);
  };

  const activeCount = countActiveFilters(filter);

  const actionOptions = useMemo(() => {
    const list = actionsQuery.data?.actions ?? [];
    return list.map((a) => {
      const def = ACTION_DESCRIPTORS[a];
      return { value: a, label: def ? t(def.labelKey) : a };
    });
  }, [actionsQuery.data, t]);

  if (!canView) {
    return (
      <Alert color="gray" icon={<IconAlertCircle size={16} />}>
        {t('auditLog.accessDenied')}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={2}>{t('auditLog.title')}</Title>
          <Text c="dimmed" size="sm">
            {t('auditLog.subtitle')}
          </Text>
        </div>
        <Tooltip label={t('auditLog.refresh')}>
          <ActionIcon
            variant="default"
            onClick={() => void pageQuery.refetch()}
            loading={pageQuery.isFetching}
          >
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <Card>
        <Stack gap="sm">
          <Group justify="space-between">
            <Group gap={6}>
              <Text size="sm" fw={600}>
                {t('auditLog.filter')}
              </Text>
              {activeCount > 0 && (
                <Badge color="brand" variant="light" size="sm">
                  {t('auditLog.activeCount', { n: activeCount })}
                </Badge>
              )}
            </Group>
            <Button
              size="xs"
              variant="subtle"
              leftSection={
                filtersOpen ? <IconFilterOff size={14} /> : <IconFilter size={14} />
              }
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? t('auditLog.collapse') : t('auditLog.expand')}
            </Button>
          </Group>
          <Collapse in={filtersOpen}>
            <Stack gap="md">
              <Grid>
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <MultiSelect
                    label={t('auditLog.actionLabel')}
                    placeholder={
                      filter.action
                        ? undefined
                        : actionsQuery.isLoading
                          ? t('auditLog.actionPlaceholderLoading')
                          : t('auditLog.actionPlaceholder')
                    }
                    data={actionOptions}
                    value={filter.action ? [filter.action] : []}
                    onChange={(v) =>
                      setFilter((f) => ({ ...f, action: v[0] ?? '' }))
                    }
                    searchable
                    clearable
                    maxValues={1}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <TextInput
                    label={t('auditLog.actorEmailLabel')}
                    placeholder={t('auditLog.actorEmailPlaceholder')}
                    value={filter.actorEmail}
                    onChange={(e) =>
                      setFilter((f) => ({ ...f, actorEmail: e.currentTarget.value }))
                    }
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <MultiSelect
                    label={t('auditLog.actorScopeLabel')}
                    placeholder={filter.actorScope.length > 0 ? undefined : t('auditLog.actorScopePlaceholder')}
                    data={[
                      { value: 'internal', label: t('auditLog.actorScopeInternal') },
                      { value: 'external', label: t('auditLog.actorScopeExternal') },
                      { value: 'system', label: t('auditLog.actorScopeSystem') },
                    ]}
                    value={filter.actorScope}
                    onChange={(v) =>
                      setFilter((f) => ({
                        ...f,
                        actorScope: v as Array<'internal' | 'external' | 'system'>,
                      }))
                    }
                    clearable
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <TextInput
                    label={t('auditLog.targetLabel')}
                    placeholder={t('auditLog.targetPlaceholder')}
                    value={filter.target}
                    onChange={(e) =>
                      setFilter((f) => ({ ...f, target: e.currentTarget.value }))
                    }
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <DatePickerInput
                    type="range"
                    label={t('auditLog.dateLabel')}
                    placeholder={t('auditLog.datePlaceholder')}
                    value={filter.dateRange}
                    onChange={(v) =>
                      setFilter((f) => ({
                        ...f,
                        dateRange: v as [Date | null, Date | null],
                      }))
                    }
                    clearable
                  />
                </Grid.Col>
              </Grid>
              <Group justify="flex-end">
                <Button variant="default" onClick={clearFilters}>
                  {t('auditLog.reset')}
                </Button>
                <Button onClick={applyFilters}>{t('auditLog.apply')}</Button>
              </Group>
            </Stack>
          </Collapse>
        </Stack>
      </Card>

      <Card p={0}>
        {pageQuery.isLoading && (
          <Center py="xl">
            <Loader />
          </Center>
        )}
        {pageQuery.error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} m="md">
            {pageQuery.error instanceof ApiCallError
              ? `${pageQuery.error.code}: ${pageQuery.error.message}`
              : t('auditLog.loadFailed')}
          </Alert>
        )}
        {!pageQuery.isLoading && !pageQuery.error && (
          <DataTable
            tableId="audit-log"
            columns={[
              { key: 'toggle', width: 40, fixed: true },
              { key: 'time', label: t('auditLog.colTime'), width: 170 },
              { key: 'activity', label: t('auditLog.colActivity'), width: 520 },
              { key: 'actor', label: t('auditLog.colActor'), width: 260 },
              { key: 'type', label: t('auditLog.colType'), width: 120 },
            ]}
          >
                {pageQuery.data?.rows.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text c="dimmed" ta="center" py="lg">
                        {t('auditLog.empty')}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {pageQuery.data?.rows.map((r) => (
                  <AuditRow
                    key={r.id}
                    row={r}
                    expanded={expanded.has(r.id)}
                    onToggle={() =>
                      setExpanded((prev) => {
                        const next = new Set(prev);
                        if (next.has(r.id)) next.delete(r.id);
                        else next.add(r.id);
                        return next;
                      })
                    }
                  />
                ))}
          </DataTable>
        )}

        <Group justify="space-between" p="md">
          <Text c="dimmed" size="xs">
            {t('auditLog.pageRows', { page: pageIdx + 1, count: pageQuery.data?.rows.length ?? 0 })}
          </Text>
          <Group gap="xs">
            <Button
              variant="default"
              size="xs"
              disabled={pageIdx === 0}
              onClick={prevPage}
            >
              {t('auditLog.previous')}
            </Button>
            <Button
              variant="default"
              size="xs"
              disabled={!pageQuery.data?.hasMore}
              onClick={nextPage}
            >
              {t('auditLog.next')}
            </Button>
          </Group>
        </Group>
      </Card>
    </Stack>
  );
}

function AuditRow({
  row,
  expanded,
  onToggle,
}: {
  row: AuditLogRow;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const [showRaw, setShowRaw] = useState(false);
  const descriptor = describeAction(row.action, row.after, t);
  const Icon = descriptor.icon;

  return (
    <>
      <Table.Tr onClick={onToggle} style={{ cursor: 'pointer' }}>
        <Table.Td>
          {expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
        </Table.Td>
        <Table.Td style={{ whiteSpace: 'nowrap' }}>
          <Text size="xs" ff="monospace">{formatGmt7(row.at)}</Text>
        </Table.Td>
        <Table.Td>
          <Group gap="sm" wrap="nowrap" align="flex-start">
            <Badge
              color={descriptor.color}
              variant="light"
              size="lg"
              leftSection={<Icon size={14} />}
              radius="sm"
            >
              {descriptor.label}
            </Badge>
            {descriptor.narrative && (
              <Text size="sm" c="dimmed" style={{ flex: 1 }}>
                {descriptor.narrative}
              </Text>
            )}
          </Group>
        </Table.Td>
        <Table.Td>
          <Stack gap={0}>
            <Text size="sm" fw={500}>{row.actorEmail}</Text>
            <Text size="xs" c="dimmed" ff="monospace">{row.actorIp ?? t('auditLog.ipUndetected')}</Text>
          </Stack>
        </Table.Td>
        <Table.Td>
          <Badge color={scopeColor(row.actorScope)} variant="light" size="sm">
            {scopeLabel(row.actorScope, t)}
          </Badge>
        </Table.Td>
      </Table.Tr>
      {expanded && (
        <Table.Tr>
          <Table.Td colSpan={5} style={{ background: 'var(--mantine-color-default-hover)' }}>
            <Stack p="md" gap="md">
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                  {t('auditLog.detailActivity')}
                </Text>
                {renderAfterFields(row.after, t)}
              </div>

              {row.before != null && (
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                    {t('auditLog.previousValue')}
                  </Text>
                  {renderAfterFields(row.before, t)}
                </div>
              )}

              <Group gap="xl">
                {row.target && (
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('auditLog.target')}</Text>
                    <Text size="xs" ff="monospace">{row.target}</Text>
                  </div>
                )}
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('auditLog.auditId')}</Text>
                  <Text size="xs" ff="monospace">#{row.id}</Text>
                </div>
                {row.requestId && (
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('auditLog.requestId')}</Text>
                    <Text size="xs" ff="monospace">{row.requestId}</Text>
                  </div>
                )}
              </Group>

              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconCode size={14} />}
                onClick={() => setShowRaw((s) => !s)}
                style={{ alignSelf: 'flex-start' }}
              >
                {showRaw ? t('auditLog.hideRawJson') : t('auditLog.showRawJson')}
              </Button>

              {showRaw && (
                <Grid>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                      {t('auditLog.rawBefore')}
                    </Text>
                    <JsonView value={row.before} maxHeight={260} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                      {t('auditLog.rawAfter')}
                    </Text>
                    <JsonView value={row.after} maxHeight={260} />
                  </Grid.Col>
                </Grid>
              )}
            </Stack>
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}
